import { Input, cn, useInput } from "@heroui/react";
import { t } from "@lingui/core/macro";
import type React from "react";
import { type PropsWithChildren, useCallback, useState } from "react";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useField } from "remix-validated-form";
import { getFormErrorMessage } from "~/schema/translation";

type InputWrapperProps = PropsWithChildren & {
  label?: React.ReactNode;
  altLabel?: React.ReactNode;
  noErrorIcon?: boolean;
};

type Props = {
  onChange?: (phone: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  name: string;
  variant?: "bordered" | "underlined";
  isRequired?: boolean;
} & InputWrapperProps;

const getInitialValue = (value: string | undefined) => {
  if (!value) return "";
  // need to pass default country to help parse the phone number
  const phone = parsePhoneNumber(value, { defaultCountry: "CA" });

  return phone ? phone.number : "";
};

export const FormPhone: React.FC<Props> = ({ onChange, defaultValue, name, variant, ...props }) => {
  // // here parse the phone number because supabase does not return the + based on the E164 standard
  const [value, setValue] = useState(getInitialValue(defaultValue));
  const { error } = useField(name);

  const changeVal = useCallback(
    (v?: string) => {
      setValue(v || "");
      if (onChange) onChange(v || "");
    },
    [setValue, onChange],
  );

  const { getInputWrapperProps, getErrorMessageProps, getBaseProps } = useInput(props);
  const inputWrapperProps = getInputWrapperProps();

  return (
    <div {...getBaseProps()}>
      <div
        {...inputWrapperProps}
        className={cn(
          inputWrapperProps.className,
          "group",
          !!error &&
            "bg-danger-50 group-hover:bg-danger-50 group-focus:bg-danger-50 group-active:bg-danger-50",
        )}
      >
        <PhoneInput
          {...props}
          className={cn("w-full border-none", !!error && "")}
          placeholder={"(514)-123-4567"}
          disabled={props.disabled}
          focusInputOnCountrySelection={false}
          value={value}
          withCountryCallingCode
          defaultCountry={"CA"}
          onChange={changeVal}
          // next ui props
          isInvalid={!!error}
          classNames={{
            inputWrapper: "rounded-none border-none shadow-none !bg-transparent",
            input: "rounded-none border-none shadow-none",
          }}
          inputComponent={Input}
          label={t`Phone`}
          data-testid={name}
        />

        <input
          type="text"
          className="hidden"
          defaultValue={value}
          name={name}
        />
      </div>
      {error && (
        <div className="mt-1">
          <div {...getErrorMessageProps()}>{getFormErrorMessage(error)}</div>
        </div>
      )}
    </div>
  );
};
